import BaseService from './BaseService';
import {
  CIAd,
  CIAdDetail,
  CIAdListResponse,
  EnumValue,
  SimilarAdResult,
  SimilarSegmentResult,
  Product,
  ProductBrief,
  PatternRecommendation,
  StoryboardConfig,
  Storyboard,
  StoryboardComment,
  StoryboardSummary,
  StoryboardSegment,
  StoryboardHistoryRow,
  StoryboardProductSummary,
  TemplateAdUsage,
  ProductPromotion,
  MarketingBriefResponse,
  EnqueueGenerationResult,
  SendToCreatorResult,
  ScriptDelivery,
} from 'pages/VideoCreative/types';

class VideoCreativeService extends BaseService {
  private basePath = 'v1/video-creative';

  // Ads
  async listAds(params: {
    keywordId?: string;
    limit?: number;
    offset?: number;
    awarenessLevel?: string;
    hookDevice?: string;
    persuasionAngle?: string;
    proofType?: string;
    narrativeStructure?: string;
    primaryEmotion?: string;
    presenter?: string;
    productionFormat?: string;
    tone?: string;
    offerType?: string;
    ctaType?: string;
    startDateFrom?: string;
    startDateTo?: string;
  } = {}): Promise<CIAdListResponse | null> {
    await this._getAccessToken();
    return this.handleHttpRequest(
      () => this.http.get(`${this.basePath}/ads`, { params }),
      'Failed to fetch CI ads',
    );
  }

  async getAd(adId: string, { refresh }: { refresh?: boolean } = {}): Promise<CIAdDetail | null> {
    await this._getAccessToken();
    const url = refresh
      ? `${this.basePath}/ads/${adId}?refresh=true`
      : `${this.basePath}/ads/${adId}`;
    return this.handleHttpRequest(
      () => this.http.get(url),
      'Failed to fetch CI ad',
    );
  }

  async triggerExtraction(adId: string, analysisIds?: string[], model?: string): Promise<any> {
    await this._getAccessToken();
    return this.handleHttpRequest(
      () => this.http.post(`${this.basePath}/ads/${adId}/extract`, { analysisIds, model }),
      'Failed to trigger extraction',
    );
  }

  // Enums
  async listEnums(enumType?: string): Promise<{ enums: EnumValue[] } | null> {
    await this._getAccessToken();
    return this.handleHttpRequest(
      () => this.http.get(`${this.basePath}/enums`, { params: enumType ? { enumType } : {} }),
      'Failed to fetch enums',
    );
  }

  async createEnum(data: { enumType: string; enumValue: string; displayName?: string; sortOrder?: number }): Promise<EnumValue | null> {
    await this._getAccessToken();
    return this.handleHttpRequest(
      () => this.http.post(`${this.basePath}/enums`, data),
      'Failed to create enum',
    );
  }

  async updateEnum(enumId: string, data: Partial<EnumValue>): Promise<EnumValue | null> {
    await this._getAccessToken();
    return this.handleHttpRequest(
      () => this.http.put(`${this.basePath}/enums/${enumId}`, data),
      'Failed to update enum',
    );
  }

  // Similarity
  async findSimilarAds(adId: string, limit = 10): Promise<{ adId: string; similar: SimilarAdResult[] } | null> {
    await this._getAccessToken();
    return this.handleHttpRequest(
      () => this.http.get(`${this.basePath}/ads/${adId}/similar`, { params: { limit } }),
      'Failed to find similar ads',
    );
  }

  async findSimilarSegments(segmentId: string, limit = 10, segmentRole?: string): Promise<{ segmentId: string; similar: SimilarSegmentResult[] } | null> {
    await this._getAccessToken();
    return this.handleHttpRequest(
      () => this.http.get(`${this.basePath}/segments/${segmentId}/similar`, { params: { limit, segmentRole } }),
      'Failed to find similar segments',
    );
  }

  // Product Mapping
  async listProducts(
    store?: string,
    { hasMarketingBrief = false }: { hasMarketingBrief?: boolean } = {},
  ): Promise<{ products: Product[] } | null> {
    await this._getAccessToken();
    const params: Record<string, string> = {};
    if (store) params.store = store;
    if (hasMarketingBrief) params.hasMarketingBrief = 'true';
    return this.handleHttpRequest(
      () => this.http.get(`${this.basePath}/products`, { params }),
      'Failed to fetch products',
    );
  }

  async getProductBrief(productId: number): Promise<{ brief: ProductBrief | null } | null> {
    await this._getAccessToken();
    return this.handleHttpRequest(
      () => this.http.get(`${this.basePath}/products/${productId}/brief`),
      'Failed to fetch product brief',
    );
  }

  async updateProductBrief(productId: number, briefText?: string, store?: string): Promise<any> {
    await this._getAccessToken();
    return this.handleHttpRequest(
      () => this.http.put(`${this.basePath}/products/${productId}/brief`, { briefText, store }),
      'Failed to update product brief',
    );
  }

  // Active promotions for a product. Cheap cached read.
  async getProductPromotions(productId: number): Promise<{ promotions: ProductPromotion[] } | null> {
    await this._getAccessToken();
    return this.handleHttpRequest(
      () => this.http.get(`${this.basePath}/products/${productId}/promotions`),
      'Failed to load product promotions',
    );
  }

  // Force-refresh promotions for a product. Runs the full fetch (Shopify
  // GraphQL discounts + variant markdown + PDP scrape) and replaces the
  // cached rows. Synchronous; expect a few seconds.
  async refreshProductPromotions(productId: number): Promise<{ promotions: ProductPromotion[] } | null> {
    await this._getAccessToken();
    return this.handleHttpRequest(
      () => this.http.post(`${this.basePath}/products/${productId}/promotions/refresh`),
      'Failed to refresh product promotions',
    );
  }

  async getMarketingBrief(productId: number): Promise<MarketingBriefResponse | null> {
    await this._getAccessToken();
    return this.handleHttpRequest(
      () => this.http.get(`${this.basePath}/products/${productId}/marketing-brief`),
      'Failed to fetch marketing brief',
    );
  }

  async updateMarketingBrief(productId: number, brief: unknown): Promise<MarketingBriefResponse | null> {
    await this._getAccessToken();
    return this.handleHttpRequest(
      () => this.http.put(`${this.basePath}/products/${productId}/marketing-brief`, { brief }),
      'Failed to update marketing brief',
    );
  }

  async listMarketingBriefVersions(productId: number): Promise<{ versions: Array<{
    briefId: string; version: number; parentBriefId: string | null;
    editSource: string | null; model: string | null;
    schemaVersion: string | null; createdAt: string; updatedAt: string;
  }> } | null> {
    await this._getAccessToken();
    return this.handleHttpRequest(
      () => this.http.get(`${this.basePath}/products/${productId}/marketing-brief/versions`),
      'Failed to list marketing brief versions',
    );
  }

  async getPatternRecommendations(
    productId: number,
    opts: { limit?: number; startDateFrom?: string; startDateTo?: string; minGrade?: number } = {},
  ): Promise<PatternRecommendation | null> {
    await this._getAccessToken();
    const { limit = 20, startDateFrom, startDateTo, minGrade } = opts;
    const params: Record<string, string | number> = { limit };
    if (startDateFrom) params.startDateFrom = startDateFrom;
    if (startDateTo) params.startDateTo = startDateTo;
    if (typeof minGrade === 'number' && Number.isFinite(minGrade)) params.minGrade = minGrade;
    return this.handleHttpRequest(
      () => this.http.get(`${this.basePath}/products/${productId}/patterns`, { params }),
      'Failed to fetch pattern recommendations',
    );
  }

  // Edit the LLM prompt on a SAVED storyboard. Setting it via this route
  // marks the row prompt_edited=TRUE so the worker uses the stored text
  // verbatim at generation time instead of re-assembling from the config.
  async updateStoryboardPrompt(storyboardId: string, promptText: string): Promise<{ storyboardId: string; promptEdited: boolean } | null> {
    await this._getAccessToken();
    return this.handleHttpRequest(
      () => this.http.patch(`${this.basePath}/storyboards/${storyboardId}/prompt`, { promptText }),
      'Failed to update storyboard prompt',
    );
  }

  // Change the generation model for a not-yet-generated script (SAVED/FAILED).
  // The next generation uses the new model.
  // Persist a human edit to a DONE storyboard's read-through script. The edited
  // text overrides the generated one everywhere it's read; the original is kept
  // server-side so it can be viewed later.
  async updateStoryboardFlatScript(storyboardId: string, flatScript: string): Promise<{ storyboardId: string; flatScriptEdited: boolean; segmentsSynced?: boolean; updatedSegments?: number; syncReason?: string } | null> {
    await this._getAccessToken();
    return this.handleHttpRequest(
      () => this.http.patch(`${this.basePath}/storyboards/${storyboardId}/flat-script`, { flatScript }),
      'Failed to update read-through script',
    );
  }

  async updateStoryboardModel(storyboardId: string, model: string): Promise<{ storyboardId: string; generationModel: string } | null> {
    await this._getAccessToken();
    return this.handleHttpRequest(
      () => this.http.patch(`${this.basePath}/storyboards/${storyboardId}/model`, { model }),
      'Failed to update storyboard model',
    );
  }

  // Storyboard Generation — end-to-end (save + enqueue/chain). Async: returns
  // an enqueue result, not the generated storyboard. Watch progress in the
  // Scripts tab.
  async generateStoryboard(productId: number, config: StoryboardConfig): Promise<EnqueueGenerationResult | null> {
    await this._getAccessToken();
    return this.handleHttpRequest(
      () => this.http.post(`${this.basePath}/products/${productId}/storyboard`, config),
      'Failed to generate storyboard',
    );
  }

  // Save a generation request without running the LLM — captures the config +
  // assembled prompt as a SAVED storyboard row to generate later.
  async saveStoryboardRequest(productId: number, config: StoryboardConfig): Promise<{ storyboardId: string } | null> {
    await this._getAccessToken();
    return this.handleHttpRequest(
      () => this.http.post(`${this.basePath}/products/${productId}/storyboard/save`, config),
      'Failed to save storyboard request',
    );
  }

  // Batch-create script requests against a single product — one SAVED row per
  // config. With `generate: true` the backend enqueues/chains each row;
  // un-extracted templates get their pipeline kicked off automatically.
  async saveStoryboardRequestsBatch(
    productId: number,
    configs: StoryboardConfig[],
    opts: { generate?: boolean } = {},
  ): Promise<{ storyboardIds: string[]; adsNeedingPipeline: string[] } | null> {
    await this._getAccessToken();
    return this.handleHttpRequest(
      () => this.http.post(`${this.basePath}/products/${productId}/storyboards/save-batch`, {
        configs,
        generate: !!opts.generate,
      }),
      'Failed to create script requests',
    );
  }

  // Enqueue generation for a previously-saved request. If template ads aren't
  // extraction-DONE, the backend kicks off the pipeline and marks the row
  // auto_generate_when_ready=TRUE; the extraction-completed listener enqueues
  // generation when the templates are ready.
  async generateSavedStoryboard(storyboardId: string): Promise<EnqueueGenerationResult | null> {
    await this._getAccessToken();
    return this.handleHttpRequest(
      () => this.http.post(`${this.basePath}/storyboards/${storyboardId}/generate`),
      'Failed to generate storyboard',
    );
  }

  // Build-and-return-only: returns the assembled LLM prompt for the given
  // config WITHOUT calling Claude or persisting anything. Powers the
  // "Preview LLM Prompt" dialog.
  async previewStoryboardPrompt(
    productId: number, config: StoryboardConfig,
  ): Promise<{ prompt: string; model: string } | null> {
    await this._getAccessToken();
    return this.handleHttpRequest(
      () => this.http.post(`${this.basePath}/products/${productId}/storyboard/preview-prompt`, config),
      'Failed to preview prompt',
    );
  }

  async getStoryboard(storyboardId: string): Promise<{ storyboard: Storyboard } | null> {
    await this._getAccessToken();
    return this.handleHttpRequest(
      () => this.http.get(`${this.basePath}/storyboards/${storyboardId}`),
      'Failed to fetch storyboard',
    );
  }

  // Cross-product history for the Generated Ads tab. `productId` scopes
  // the list to a single product; `templateAdId` scopes to storyboards
  // that reference a specific ad as a template. Omit to get everything.
  async listAllStoryboards(params: { limit?: number; offset?: number; productId?: number; templateAdId?: string; tag?: string[] } = {}): Promise<{ storyboards: StoryboardHistoryRow[]; total: number } | null> {
    await this._getAccessToken();
    return this.handleHttpRequest(
      () => this.http.get(`${this.basePath}/storyboards`, { params }),
      'Failed to list storyboards',
    );
  }

  // Tags (free-form, multiple per generated script)
  async listStoryboardTags(): Promise<{ tags: string[] } | null> {
    await this._getAccessToken();
    return this.handleHttpRequest(
      () => this.http.get(`${this.basePath}/storyboard-tags`),
      'Failed to fetch script tags',
    );
  }

  async addStoryboardTag(storyboardId: string, tag: string): Promise<{ tags: string[] } | null> {
    await this._getAccessToken();
    return this.handleHttpRequest(
      () => this.http.post(`${this.basePath}/storyboards/${storyboardId}/tags`, { tag }),
      'Failed to add tag',
    );
  }

  async removeStoryboardTag(storyboardId: string, tag: string): Promise<{ tags: string[] } | null> {
    await this._getAccessToken();
    return this.handleHttpRequest(
      () => this.http.delete(`${this.basePath}/storyboards/${storyboardId}/tags/${encodeURIComponent(tag)}`),
      'Failed to remove tag',
    );
  }

  // Comments / notes on a generated script (many per script, editable + deletable).
  // Each mutation returns the full, freshly-read comment list so callers can
  // replace their local state with the server's source of truth.
  async addStoryboardComment(storyboardId: string, body: string): Promise<{ comments: StoryboardComment[] } | null> {
    await this._getAccessToken();
    return this.handleHttpRequest(
      () => this.http.post(`${this.basePath}/storyboards/${storyboardId}/comments`, { body }),
      'Failed to add comment',
    );
  }

  async updateStoryboardComment(storyboardId: string, commentId: string, body: string): Promise<{ comments: StoryboardComment[] } | null> {
    await this._getAccessToken();
    return this.handleHttpRequest(
      () => this.http.patch(`${this.basePath}/storyboards/${storyboardId}/comments/${encodeURIComponent(commentId)}`, { body }),
      'Failed to update comment',
    );
  }

  async deleteStoryboardComment(storyboardId: string, commentId: string): Promise<{ comments: StoryboardComment[] } | null> {
    await this._getAccessToken();
    return this.handleHttpRequest(
      () => this.http.delete(`${this.basePath}/storyboards/${storyboardId}/comments/${encodeURIComponent(commentId)}`),
      'Failed to delete comment',
    );
  }

  // Aggregate template-ad usage. Returns one row per ad that has ever
  // been used as a template, with the total count and last-used date.
  // Used by Product Mapping to dim rows for ads already used.
  async getTemplateAdUsage(): Promise<{ usage: TemplateAdUsage[] } | null> {
    await this._getAccessToken();
    return this.handleHttpRequest(
      () => this.http.get(`${this.basePath}/storyboards/template-usage`),
      'Failed to load template usage',
    );
  }

  // Distinct products that have at least one storyboard — used to seed
  // the product filter dropdown on the Generated Ads tab.
  async listStoryboardProducts(): Promise<{ products: StoryboardProductSummary[] } | null> {
    await this._getAccessToken();
    return this.handleHttpRequest(
      () => this.http.get(`${this.basePath}/storyboards/products`),
      'Failed to list storyboard products',
    );
  }

  // Cleanly delete a storyboard and all its segments.
  async deleteStoryboard(storyboardId: string): Promise<boolean> {
    await this._getAccessToken();
    const result = await this.handleHttpRequest(
      () => this.http.delete(`${this.basePath}/storyboards/${storyboardId}`),
      'Failed to delete storyboard',
    );
    return result !== null;
  }

  async listStoryboards(productId: number): Promise<{ storyboards: StoryboardSummary[] } | null> {
    await this._getAccessToken();
    return this.handleHttpRequest(
      () => this.http.get(`${this.basePath}/products/${productId}/storyboards`),
      'Failed to fetch storyboards',
    );
  }

  async updateStoryboardSegment(storyboardId: string, segmentId: string, data: Partial<StoryboardSegment>): Promise<{ segment: StoryboardSegment } | null> {
    await this._getAccessToken();
    return this.handleHttpRequest(
      () => this.http.put(`${this.basePath}/storyboards/${storyboardId}/segments/${segmentId}`, data),
      'Failed to update segment',
    );
  }

  // Send a finished script to a creator: posts the rendered brief PDF + creator/
  // script details as multipart form data. Unlike most methods here, this lets
  // errors propagate (no handleHttpRequest swallow) so the dialog can show the
  // real failure reason — e.g. "no Trello member matches this creator".
  async sendStoryboardToCreator(
    storyboardId: string,
    data: { creatorId: string; scriptName: string; scriptText: string; productName?: string; pdfBlob: Blob; pdfFilename?: string },
  ): Promise<SendToCreatorResult> {
    await this._getAccessToken();
    const form = new FormData();
    form.append('creatorId', data.creatorId);
    form.append('scriptName', data.scriptName);
    form.append('scriptText', data.scriptText || '');
    form.append('productName', data.productName || '');
    form.append('pdf', data.pdfBlob, data.pdfFilename || 'creator-brief.pdf');
    const resp = await this.http.post(
      `${this.basePath}/storyboards/${storyboardId}/send-to-creator`,
      form,
    );
    return resp.data as SendToCreatorResult;
  }

  // Delivery history for a script (every send to a creator), newest first.
  async listStoryboardDeliveries(storyboardId: string): Promise<{ deliveries: ScriptDelivery[] } | null> {
    await this._getAccessToken();
    return this.handleHttpRequest(
      () => this.http.get(`${this.basePath}/storyboards/${storyboardId}/deliveries`),
      'Failed to load deliveries',
    );
  }

}

export default VideoCreativeService;
